import { useTheme } from "../ThemeContext";
import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell } from "recharts";
import { fmt, STATUS, DONE_STATUS, calcKpis, PAY_LABELS, PAY_COLORS } from "../theme";
import { KpiCard, DataTable, Card, EmptyState, ChartTooltip } from "../components/UI";
import { exportExcel, exportPDF, fmtMoeda, fmtData, fmtDataCurta, getTurno, filtrarPorPeriodo } from "../utils/exportUtils";

const TURNOS = [
  { key: "Manha", label: "Manha (6h-12h)", icon: "🌅", color: "#F59E0B" },
  { key: "Tarde", label: "Tarde (12h-18h)", icon: "☀️", color: "#3B82F6" },
  { key: "Noite", label: "Noite (18h-6h)", icon: "🌙", color: "#8B5CF6" },
];

const PERIODOS = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mes" },
  { key: "ano", label: "Ano" },
  { key: "custom", label: "Personalizado" },
];

export default function RelPedidos({ orders, sub, lang }) {
  const { theme: C } = useTheme();
  const [periodo, setPeriodo] = useState("mes");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const POR_PAGINA = 20;

  const filtrados = useMemo(() => filtrarPorPeriodo(orders, periodo, dataInicio, dataFim), [orders, periodo, dataInicio, dataFim]);

  const pedVendidos = filtrados.filter(o => DONE_STATUS.includes(o.status));
  const pedCanc = filtrados.filter(o => o.status === "cancelado");
  const pedAtivos = filtrados.filter(o => !DONE_STATUS.includes(o.status) && o.status !== "cancelado");

  // ── Dados por turno ──
  const turnoData = useMemo(() => {
    const map = { Manha: { ped: 0, fat: 0, canc: 0 }, Tarde: { ped: 0, fat: 0, canc: 0 }, Noite: { ped: 0, fat: 0, canc: 0 } };
    filtrados.forEach(o => {
      const t = getTurno(o.criado_em);
      if (map[t]) {
        map[t].ped++;
        if (DONE_STATUS.includes(o.status)) map[t].fat += Number(o.total || 0);
        if (o.status === "cancelado") map[t].canc++;
      }
    });
    return map;
  }, [filtrados]);

  // ── Dados por hora ──
  const hourly = useMemo(() => {
    const hMap = {};
    filtrados.forEach(o => { if (o.criado_em) { const h = new Date(o.criado_em).getHours(); hMap[h] = (hMap[h] || 0) + 1; } });
    return Array.from({ length: 18 }, (_, i) => ({ h: `${i + 6}h`, v: hMap[i + 6] || 0 }));
  }, [filtrados]);

  // ── Dados por dia ──
  const diasData = useMemo(() => {
    const dm = {};
    filtrados.forEach(o => { const d = o.criado_em?.slice(0, 10); if (d) dm[d] = (dm[d] || 0) + 1; });
    return Object.entries(dm).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => ({ dia: d.slice(5), pedidos: v }));
  }, [filtrados]);

  // ── Dados por status ──
  const statusData = useMemo(() => {
    const sm = {};
    filtrados.forEach(o => { sm[o.status] = (sm[o.status] || 0) + 1; });
    return Object.entries(sm).map(([k, v]) => ({ name: STATUS[k]?.label || k, value: v, color: STATUS[k]?.color || C.textMuted }));
  }, [filtrados]);

  // ── Dados por forma de pagamento ──
  const pagData = useMemo(() => {
    const pm = {};
    pedVendidos.forEach(o => { const k = o.forma_pagamento || "outro"; pm[k] = (pm[k] || 0) + Number(o.total || 0); });
    return Object.entries(pm).map(([k, v]) => ({ name: PAY_LABELS[k] || k, value: v, color: PAY_COLORS[k] || "#888" }));
  }, [pedVendidos]);

  // ── Motivos de cancelamento ──
  const motivosCanc = useMemo(() => {
    const mm = {};
    pedCanc.forEach(o => { const m = o.motivo_cancelamento || "Sem motivo informado"; mm[m] = (mm[m] || 0) + 1; });
    return Object.entries(mm).sort(([, a], [, b]) => b - a).map(([m, c]) => ({ motivo: m, qtd: c }));
  }, [pedCanc]);

  const k = calcKpis(filtrados);
  const kVend = calcKpis(pedVendidos);
  const taxaCanc = filtrados.length > 0 ? ((pedCanc.length / filtrados.length) * 100).toFixed(1) : "0";

  // ── Barra de filtro ──
  const iS = { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 12, outline: "none" };

  const FilterBar = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16, padding: 12, background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginRight: 4 }}>Periodo:</span>
      {PERIODOS.map(p => (
        <button key={p.key} onClick={() => { setPeriodo(p.key); setPaginaAtual(1); }}
          style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${periodo === p.key ? C.red : C.border}`,
            background: periodo === p.key ? C.red + "18" : "transparent", color: periodo === p.key ? C.red : C.textMuted,
            cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          {p.label}
        </button>
      ))}
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={iS} />
          <span style={{ color: C.textMuted, fontSize: 11 }}>ate</span>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={iS} />
        </div>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <button onClick={handleExportExcel} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.green}40`, background: `${C.green}12`, color: C.green, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          Excel
        </button>
        <button onClick={handleExportPDF} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.red}40`, background: `${C.red}12`, color: C.red, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          PDF
        </button>
      </div>
    </div>
  );

  // ── Exportações ──
  const handleExportExcel = () => {
    const headers = ["Codigo", "Status", "Pagamento", "Subtotal", "Taxa Entrega", "Total", "Cliente", "Turno", "Data"];
    const rows = filtrados.map(o => [
      o.codigo, STATUS[o.status]?.label || o.status, PAY_LABELS[o.forma_pagamento] || o.forma_pagamento,
      Number(o.subtotal || 0).toFixed(2), Number(o.taxa_entrega || 0).toFixed(2), Number(o.total || 0).toFixed(2),
      o.cliente_nome || `#${o.cliente_id}`, getTurno(o.criado_em), fmtData(o.criado_em)
    ]);
    exportExcel(headers, rows, "relatorio_pedidos");
  };

  const handleExportPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val blue">${k.total}</div><div class="kpi-label">Total Pedidos</div></div>
      <div class="kpi"><div class="kpi-val green">${fmtMoeda(kVend.fat)}</div><div class="kpi-label">Faturamento</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(kVend.avg)}</div><div class="kpi-label">Ticket Medio</div></div>
      <div class="kpi"><div class="kpi-val red">${pedCanc.length} (${taxaCanc}%)</div><div class="kpi-label">Cancelados</div></div>
    </div>`;

    const turnoHTML = `<h2>Desempenho por Turno</h2><table>
      <tr><th>Turno</th><th>Pedidos</th><th>Faturamento</th><th>Cancelados</th></tr>
      ${TURNOS.map(t => `<tr><td>${t.icon} ${t.label}</td><td>${turnoData[t.key].ped}</td><td>${fmtMoeda(turnoData[t.key].fat)}</td><td>${turnoData[t.key].canc}</td></tr>`).join("")}
    </table>`;

    const tabelaHTML = `<h2>Detalhamento de Pedidos (${filtrados.length})</h2><table>
      <tr><th>Codigo</th><th>Status</th><th>Pagamento</th><th>Total</th><th>Turno</th><th>Data</th></tr>
      ${filtrados.slice(0, 100).map(o => `<tr>
        <td>#${o.codigo}</td><td>${STATUS[o.status]?.label || o.status}</td>
        <td>${PAY_LABELS[o.forma_pagamento] || o.forma_pagamento || "—"}</td>
        <td>${fmtMoeda(o.total)}</td><td>${getTurno(o.criado_em)}</td><td>${fmtData(o.criado_em)}</td>
      </tr>`).join("")}
    </table>`;

    let cancHTML = "";
    if (pedCanc.length > 0) {
      cancHTML = `<h2>Cancelamentos (${pedCanc.length})</h2><table>
        <tr><th>Motivo</th><th>Quantidade</th><th>% do Total</th></tr>
        ${motivosCanc.map(m => `<tr><td>${m.motivo}</td><td>${m.qtd}</td><td>${(m.qtd / pedCanc.length * 100).toFixed(1)}%</td></tr>`).join("")}
      </table>`;
    }

    exportPDF("Relatorio de Pedidos", kpiHTML + turnoHTML + tabelaHTML + cancHTML, "relatorio_pedidos");
  };

  // ── Paginação ──
  const listaExibir = sub === "cancelados" ? pedCanc : sub === "vendidos" ? pedVendidos : filtrados;
  const totalPag = Math.ceil(listaExibir.length / POR_PAGINA);
  const listaPaginada = listaExibir.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  const Pagination = () => totalPag > 1 ? (
    <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 12 }}>
      {Array.from({ length: Math.min(totalPag, 10) }, (_, i) => (
        <button key={i} onClick={() => setPaginaAtual(i + 1)}
          style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${paginaAtual === i + 1 ? C.red : C.border}`,
            background: paginaAtual === i + 1 ? C.red : "transparent", color: paginaAtual === i + 1 ? "#fff" : C.textMuted,
            cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          {i + 1}
        </button>
      ))}
    </div>
  ) : null;

  const orderRow = (p) => (
    <tr key={p.id}>
      <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>#{p.codigo}</td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: STATUS[p.status]?.color, background: STATUS[p.status]?.bg, padding: "3px 8px", borderRadius: 6 }}>
          {STATUS[p.status]?.label || p.status}
        </span>
      </td>
      <td style={{ padding: "10px 12px", fontSize: 11, color: C.textSoft }}>{PAY_LABELS[p.forma_pagamento] || p.forma_pagamento || "—"}</td>
      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 800, color: C.green }}>{fmt(p.total)}</td>
      <td style={{ padding: "10px 12px", fontSize: 10, color: C.textMuted }}>
        {TURNOS.find(t => t.key === getTurno(p.criado_em))?.icon} {getTurno(p.criado_em)}
      </td>
      <td style={{ padding: "10px 12px", fontSize: 11, color: C.textMuted }}>{fmtData(p.criado_em)}</td>
    </tr>
  );

  // ═══ SUB: VENDIDOS ═══
  if (sub === "vendidos") {
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Vendidos" value={pedVendidos.length} color={C.green} delay={0} />
        <KpiCard label="Faturamento" value={fmt(kVend.fat)} color={C.blue} delay={.06} />
        <KpiCard label="Ticket Medio" value={fmt(kVend.avg)} color={C.orange} delay={.12} />
        <KpiCard label="Taxa Entrega" value={fmt(pedVendidos.reduce((s, o) => s + Number(o.taxa_entrega || 0), 0))} color={C.purple || "#8B5CF6"} delay={.18} />
      </div>

      {/* Turnos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {TURNOS.map(t => (
          <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
            <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{t.icon} {t.label}</div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div><div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{turnoData[t.key]?.ped || 0}</div><div style={{ fontSize: 9, color: C.textMuted }}>pedidos</div></div>
              <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmt(turnoData[t.key]?.fat || 0)}</div><div style={{ fontSize: 9, color: C.textMuted }}>faturamento</div></div>
            </div>
          </div>
        ))}
      </div>

      {/* Grafico por dia */}
      {diasData.length > 1 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Vendas por Dia</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={diasData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="pedidos" stroke={C.green} fill={C.green + "30"} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Pagamento */}
      {pagData.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, marginBottom: 16 }}>
          {pagData.map((p, i) => (
            <div key={i} style={{ background: C.card, borderRadius: 10, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: p.color }}>{fmt(p.value)}</div>
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{p.name}</div>
            </div>
          ))}
        </div>
      )}

      <Card>
        {pedVendidos.length === 0 ? <EmptyState text="Nenhum pedido vendido no periodo" /> : (<>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Pedidos Vendidos ({pedVendidos.length})</h3>
          </div>
          <DataTable headers={["Codigo", "Status", "Pagamento", "Total", "Turno", "Data"]}>
            {listaPaginada.map(orderRow)}
          </DataTable>
          <Pagination />
        </>)}
      </Card>
    </>);
  }

  // ═══ SUB: CANCELADOS ═══
  if (sub === "cancelados") {
    const perdaTotal = pedCanc.reduce((s, o) => s + Number(o.total || 0), 0);
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Cancelados" value={pedCanc.length} color={C.red} delay={0} />
        <KpiCard label="Perda Estimada" value={fmt(perdaTotal)} color={C.orange} delay={.06} />
        <KpiCard label="Taxa Cancelamento" value={taxaCanc + "%"} color={parseFloat(taxaCanc) > 5 ? C.red : C.green} delay={.12} />
        <KpiCard label="Media por Canc." value={fmt(pedCanc.length > 0 ? perdaTotal / pedCanc.length : 0)} color={C.blue} delay={.18} />
      </div>

      {/* Indicador visual de performance */}
      <div style={{ padding: 12, borderRadius: 10, marginBottom: 16,
        background: parseFloat(taxaCanc) > 10 ? `${C.red}12` : parseFloat(taxaCanc) > 5 ? "#F59E0B12" : `${C.green}12`,
        border: `1px solid ${parseFloat(taxaCanc) > 10 ? C.red + "30" : parseFloat(taxaCanc) > 5 ? "#F59E0B30" : C.green + "30"}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>{parseFloat(taxaCanc) > 10 ? "🔴" : parseFloat(taxaCanc) > 5 ? "🟡" : "🟢"}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              {parseFloat(taxaCanc) > 10 ? "Taxa de cancelamento alta — acima de 10%" : parseFloat(taxaCanc) > 5 ? "Taxa de cancelamento moderada — entre 5% e 10%" : "Taxa de cancelamento saudavel — abaixo de 5%"}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              {pedCanc.length} de {filtrados.length} pedidos cancelados no periodo
            </div>
          </div>
        </div>
      </div>

      {/* Turnos com cancelamento */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {TURNOS.map(t => {
          const cancT = turnoData[t.key]?.canc || 0;
          const pedT = turnoData[t.key]?.ped || 0;
          const pct = pedT > 0 ? ((cancT / pedT) * 100).toFixed(1) : "0";
          return (
            <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
              <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{t.icon} {t.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: cancT > 0 ? C.red : C.green }}>{cancT}</div>
              <div style={{ fontSize: 9, color: C.textMuted }}>{pct}% dos {pedT} pedidos</div>
            </div>
          );
        })}
      </div>

      {/* Motivos */}
      {motivosCanc.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Motivos de Cancelamento</h3>
          <DataTable headers={["Motivo", "Quantidade", "% do Total"]}>
            {motivosCanc.map((m, i) => (
              <tr key={i}>
                <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600 }}>{m.motivo}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 800, color: C.red }}>{m.qtd}</td>
                <td style={{ padding: "10px 12px", fontSize: 12, color: C.textMuted }}>{(m.qtd / pedCanc.length * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </DataTable>
        </Card>
      )}

      <Card>
        {pedCanc.length === 0 ? <EmptyState text="Nenhum cancelamento no periodo" /> : (<>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Pedidos Cancelados ({pedCanc.length})</h3>
          <DataTable headers={["Codigo", "Status", "Pagamento", "Total", "Turno", "Data"]}>
            {listaPaginada.map(orderRow)}
          </DataTable>
          <Pagination />
        </>)}
      </Card>
    </>);
  }

  // ═══ SUB: DIA / MES / ANO (Dashboard geral) ═══
  return (<>
    <FilterBar />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
      <KpiCard label="Total Pedidos" value={k.total} color={C.blue} delay={0} />
      <KpiCard label="Vendidos" value={pedVendidos.length} color={C.green} delay={.05} />
      <KpiCard label="Faturamento" value={fmt(kVend.fat)} color={C.orange} delay={.1} />
      <KpiCard label="Ticket Medio" value={fmt(kVend.avg)} color={C.purple || "#8B5CF6"} delay={.15} />
      <KpiCard label="Cancelados" value={`${pedCanc.length} (${taxaCanc}%)`} color={C.red} delay={.2} />
    </div>

    {/* Turnos */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
      {TURNOS.map(t => (
        <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
          <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{t.icon} {t.label}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{turnoData[t.key]?.ped || 0}</div>
              <div style={{ fontSize: 9, color: C.textMuted }}>pedidos</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmt(turnoData[t.key]?.fat || 0)}</div>
              <div style={{ fontSize: 9, color: C.textMuted }}>faturamento</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: turnoData[t.key]?.canc > 0 ? C.red : C.textMuted }}>{turnoData[t.key]?.canc || 0}</div>
              <div style={{ fontSize: 9, color: C.textMuted }}>cancelados</div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Graficos lado a lado */}
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
      {/* Pedidos por hora ou dia */}
      <Card>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{periodo === "hoje" || periodo === "ontem" ? "Por Hora" : "Por Dia"}</h3>
        <ResponsiveContainer width="100%" height={180}>
          {periodo === "hoje" || periodo === "ontem" ? (
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="h" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="v" radius={[4, 4, 0, 0]} fill={C.red} />
            </BarChart>
          ) : (
            <AreaChart data={diasData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="dia" tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.textMuted, fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="pedidos" stroke={C.blue} fill={C.blue + "30"} strokeWidth={2} />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </Card>

      {/* Status pie */}
      <Card>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Por Status</h3>
        {statusData.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <PieChart width={150} height={150}>
              <Pie data={statusData} cx={72} cy={72} innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
            </PieChart>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 8 }}>
              {statusData.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                  <span style={{ fontSize: 9, color: C.textSoft }}>{s.name} ({s.value})</span>
                </div>
              ))}
            </div>
          </div>
        ) : <EmptyState />}
      </Card>
    </div>

    {/* Tabela */}
    <Card>
      {filtrados.length === 0 ? <EmptyState text="Nenhum pedido no periodo" /> : (<>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Todos os Pedidos ({filtrados.length})</h3>
          <span style={{ fontSize: 10, color: C.textMuted }}>Pagina {paginaAtual} de {totalPag}</span>
        </div>
        <DataTable headers={["Codigo", "Status", "Pagamento", "Total", "Turno", "Data"]}>
          {listaPaginada.map(orderRow)}
        </DataTable>
        <Pagination />
      </>)}
    </Card>
  </>);
}
